import { defineStore } from 'pinia'
import { useIntervalFn } from '@vueuse/core'
import apiClient from '@/api/axios'
import { DEPOSIT_LIMITS, DEPOSIT_POLLING } from '@/constants/payments'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useAuthStore } from '@/stores/auth'

export const useDepositStore = defineStore('deposit', {
  state: () => ({
    /** active poll controller per deposit request (optional, if you want to support cancel) */
    _poll: null,
  }),

  actions: {
    /**
     * Submit deposit and start polling balance after STK push is initiated.
     * Accepts envelope:
     * {
     *   code: "Success",
     *   statusDescription: "Request is successful",
     *   data: { code: 200, message: "KES.20 Online payment initiated! ..." }
     * }
     */
    async submitOnlineDeposit(amount, extraHeaders = undefined) {
      const ns = useNotificationStore()
      const auth = useAuthStore()

      // ---- Validation ----
      const n = Number(amount)
      if (!Number.isInteger(n)) {
        ns.showNotification('Please enter a whole number amount (no decimals).', 'error')
        return { success: false, message: 'Invalid amount' }
      }
      if (n < DEPOSIT_LIMITS.min) {
        ns.showNotification(
          `Minimum deposit is ${DEPOSIT_LIMITS.currencyLabel} ${DEPOSIT_LIMITS.min}.`,
          'error'
        )
        return { success: false, message: 'Below minimum' }
      }
      if (n > DEPOSIT_LIMITS.maxPerTxn) {
        ns.showNotification(
          `Deposit cannot exceed ${DEPOSIT_LIMITS.currencyLabel} ${DEPOSIT_LIMITS.maxPerTxn} per transaction.`,
          'error'
        )
        return { success: false, message: 'Above per-transaction maximum' }
      }

      try {
        const { data: res } = await apiClient.post(
          import.meta.env.VITE_API_DEPOSIT_ENDPOINT,
          { amount: n, desc: '' },
          extraHeaders ? { headers: extraHeaders } : undefined
        )

        // Interpret success shape you provided
        const isOk =
          (res?.code === 'Success' && res?.data?.code === 200) ||
          res?.code === 200

        if (!isOk) {
          const msg = res?.data?.message || res?.message || 'Deposit failed. Please try again.'
          ns.showNotification(msg, 'error')
          return { success: false, message: msg }
        }

        const successMsg = res?.data?.message || res?.message || 'Deposit initiated.'
        ns.showNotification(successMsg, 'success')

        // kick off polling for balance change
        const initialBalance = Number(auth.balance || 0)
        this._startBalancePolling(initialBalance)

        return { success: true, message: successMsg }
      } catch (error) {
        console.error('Deposit Error:', error)
        const errMsg =
          error.response?.data?.data?.message ||
          error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred. Please try again.'
        useNotificationStore().showNotification(errMsg, 'error')
        return { success: false, message: errMsg }
      }
    },

    /**
     * Start (or restart) a VueUse interval poll for balance.
     * Stops when:
     *  - balance increases vs initialBalance, or
     *  - attempts reach DEPOSIT_POLLING.maxAttempts
     * Notifies success/warning accordingly.
     */
    _startBalancePolling(initialBalance) {
      // stop any previous poll
      this._stopBalancePolling()

      const ns = useNotificationStore()
      const auth = useAuthStore()

      let attempts = 0
      const { pause, resume, isActive } = useIntervalFn(async () => {
        attempts += 1
        try {
          await auth.fetchBalance()
          const current = Number(auth.balance || 0)

          // SUCCESS: balance increased
          if (current > Number(initialBalance)) {
            pause()
            this._poll = null
            ns.showNotification('Balance updated successfully.', 'success')
            return
          }
        } catch (e) {
          // swallow fetch errors; interceptor already notifies 401/500
          console.warn('Balance poll failed:', e)
        }

        // TIMEOUT: exceeded max attempts
        if (attempts >= DEPOSIT_POLLING.maxAttempts) {
          pause()
          this._poll = null
          ns.showNotification(
            'We could not confirm the top up yet. If you entered your MPESA PIN, your balance will update shortly.',
            'warning'
          )
        }
      }, DEPOSIT_POLLING.intervalMs, { immediate: true })

      // store a handle so we can cancel later (e.g., on logout/navigation)
      this._poll = { pause, resume, isActive }
      // ensure running
      resume()
    },

    /** Stop an active polling interval if present */
    _stopBalancePolling() {
      if (this._poll && typeof this._poll.pause === 'function') {
        this._poll.pause()
      }
      this._poll = null
    },
  },
})
